'use client';

import { useEffect, useState } from 'react';

/**
 * Dev-only staleness indicator.
 *
 * A tab left open across a server restart keeps running the bundle it loaded —
 * the HMR socket dies with the old process, so the page silently behaves like
 * old code while the server runs new code. This badge remembers the build id it
 * started with and polls /api/health; when the id changes it stops being a quiet
 * grey dot and becomes a reload button.
 *
 * Renders nothing in production.
 */
const POLL_MS = 20_000;

export function DevBuildBadge() {
    const [initialId, setInitialId] = useState<string | null>(null);
    const [currentId, setCurrentId] = useState<string | null>(null);

    useEffect(() => {
        if (process.env.NODE_ENV === 'production') return;
        let cancelled = false;

        const check = async () => {
            try {
                const res = await fetch('/api/health', { cache: 'no-store' });
                if (!res.ok) return;
                const { buildId } = await res.json();
                if (cancelled || !buildId) return;
                setCurrentId(buildId);
                setInitialId(prev => prev ?? buildId);
            } catch {
                // Server down mid-restart — keep the last known state, next tick retries.
            }
        };

        check();
        const timer = setInterval(check, POLL_MS);
        return () => { cancelled = true; clearInterval(timer); };
    }, []);

    if (process.env.NODE_ENV === 'production') return null;
    if (!initialId) return null;

    const stale = currentId !== null && currentId !== initialId;

    if (!stale) {
        return (
            <div
                className="fixed bottom-16 right-3 z-50 select-none rounded px-1.5 py-0.5 font-mono text-[10px] text-gray-400/70 bg-white/60 pointer-events-none"
                title={`Bundle của tab: ${initialId}`}
            >
                {initialId}
            </div>
        );
    }

    return (
        <button
            onClick={() => window.location.reload()}
            className="fixed bottom-16 right-3 z-50 flex items-center gap-1.5 rounded-full bg-amber-500 px-3 py-1.5 text-xs font-medium text-white shadow-lg hover:bg-amber-600"
            title={`Tab đang chạy ${initialId}, server đã sang ${currentId}`}
        >
            <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" />
            Code đã đổi — Tải lại
        </button>
    );
}
